//
// # Instance
//

// NPM Imports
import typia from "typia";

// Local Imports
import { schema } from "GenAlphaSchema";
import { exhaust, Validator } from "../errors";
import { Xform, Bbox } from "../core";
import { Ident } from "../ident";
import { ScalarOrBusName } from "./name";
import { Expr, ExprKind, ExprVariant } from "./expr";
import { Eval } from "./evals";
import { Symbol } from "./symbol";
import { Author } from "./author";
import { Connectable } from "./conns";
import { InstanceParam } from "./param";

// Re-export(s)
export { InstanceParam } from "./param";

// # Instance Conn
// Connection to an instance
export interface InstanceConn {
  portName: string;
  conn: Connectable;
}

// # Schematic Instance
export type Instance = Omit<InstanceSpec, "params"> & InstanceInner;

// # Instace "Spec"
// Public/ User/ Schema-Defined Fields
export type InstanceSpec = schema.schematic.Instance;

export const InstanceSpec = {
  // Create a "null instance"
  null: (): InstanceSpec => ({
    name: "",
    xform: Xform.default(),
    target: Ident.default(),
  }),
  // JSON validator
  validator: new Validator(typia.createValidate<InstanceSpec>()),
  // JSON stringifier
  stringify: typia.json.createStringify<InstanceSpec>(),
};

// Internally Computed Fields
export interface InstanceInner {
  conns: Array<InstanceConn>; // Connections
  params: Array<InstanceParam>; // Parameters (Required Array)
  bbox: Bbox; // Bounding Box. Especially relevant if symbol is undefined.
  symbol: Readonly<Symbol> | undefined; // Target Symbol, if resolved.
  evals: Array<Eval>; // Expression evaluations
  author: Author;
}

export const InstanceInner = {
  null: (): InstanceInner => ({
    conns: [],
    params: [],
    bbox: Bbox.empty(),
    symbol: undefined,
    evals: [],
    author: Author.User,
  }),
};
export const Instance = {
  // Create a "null instance", which refers to nothing valid, but initializes the fields.
  null: (): Instance => ({
    ...InstanceInner.null(),
    ...InstanceSpec.null(),
  }),

  fromPartial: (partial: Partial<Instance>): Instance => ({
    ...Instance.null(),
    ...partial,
  }),

  toSpec: (instance: Instance): InstanceSpec => {
    const { name, target, xform } = instance;
    const params =
      instance.params.length > 0
        ? instance.params.map((p) => InstanceParam.toTuple(p))
        : undefined;
    return {
      name,
      target,
      xform,
      params,
    };
  },

  clone: (instance: Instance): Instance => {
    // FIXME: probably need to deep-clone some of these object-valued fields sheesh
    return { ...instance };
  },

  // Evaluate all the expressions on `instance`s symbol (instance-names etc.).
  evaluateExprs: (instance: Instance): Instance => {
    const { symbol } = instance;
    if (!symbol) return instance; // No symbol, nothing to do
    instance.evals = symbol.exprLabels.map((expr) =>
      Instance.evaluateExpr(instance, expr)
    );
    return instance;
  },

  // Evaluate an `Expr` in the context of an `Instance`, returning its evaluated value.
  evaluateExpr: (inst: Instance, expr: Expr): Eval => {
    const { kind } = expr;
    switch (kind) {
      // FIXME: add some of these
      // case ExprKind.PortDebug:
      case ExprKind.CellName:
        return Expr.CellName.evaluate(expr, inst);
      case ExprKind.InstName:
        return Eval.ok(expr, inst.name);
      case ExprKind.InstParam:
        const { author } = inst;
        switch (author) {
          case Author.ExternalBuiltIn:
          case Author.Pdk:
            // Don't render these, there's often more hijinx behind them we don't comprehend yet.
            return Eval.doNotDisplay(expr);
          case Author.BuiltIn:
          case Author.User:
            return Instance.evaluateInstParamExpr(inst, expr);
          default:
            exhaust(author);
        }
      // FIXME: maybe add a few more of these
      // FIXME: `PortName` in particular comes up on the "port symbols", e.g. "basic / iopind".
      case ExprKind.PortDebug:
      case ExprKind.PortName:
      case ExprKind.Invalid:
        return Eval.doNotDisplay(expr);
      default:
        exhaust(kind);
    }
  },

  // Evaluate the value of an instance parameter, to either a string which can be displayed, or null.
  evaluateInstParamExpr: (
    instance: Instance,
    expr: ExprVariant<"inst-param">
  ): Eval => {
    //
    if (!instance.symbol) {
      return Eval.doNotDisplay(expr);
    }

    // Handle index-based parameter specs (used by builtin symbols)
    if (expr.spec.kind === "index") {
      // FIXME: are these ordered on the INSTANCE or the IMPLEMENTER?
      // Probably the latter, which we'll need to figure out how to look up
      const { index } = expr.spec;
      if (index < 1 || index >= instance.params.length + 1) {
        return Eval.doNotDisplay(expr);
      }
      const { name, value } = InstanceParam.toObject(
        instance.params[index - 1]
      );

      // Good to go, equal-sign-ify it
      // FIXME: the name-value or value-only options
      return Eval.ok(expr, `${name}=${value}`);
    }

    // Handle name-based parameter specs (used by PDK symbols)
    if (expr.spec.kind === "name") {
      const paramName = expr.spec.name;
      // Find the parameter by name in the instance's params array
      const param = instance.params.find((p) => {
        const paramObj = InstanceParam.toObject(p);
        return paramObj.name === paramName;
      });

      if (!param) {
        return Eval.doNotDisplay(expr);
      }

      const { name, value } = InstanceParam.toObject(param);
      return Eval.ok(expr, `${name}=${value}`);
    }

    // Unknown spec kind
    return Eval.doNotDisplay(expr);
  },
};

// Type alias for a valid instance name.
export type ValidInstanceName = ScalarOrBusName;
export const ValidInstanceName = ScalarOrBusName;
